import React, { useState, useContext } from 'react';
import CustomToolbarButton from './toolbar/custom-toolbar-button';
import ASEmailIcon from '../../../assets/icons/Email.svg';
// import ActionDialog from './action-dialog';
import { useSendEmail } from './use-send-email';
import ValidationUtils from '../../../react-core-lib/utils/validation-utils';
import mixpanel from 'mixpanel-browser';
import SuccessDialog from '../../UIControls/api-button/SuccessDialog';
import ConfirmationDialog from '../../UIControls/ConfirmationDialog';
import ErrorDialog from '../../UIControls/api-button/ErrorDialog';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import ProvetApi from '../../../rest-api/provet-api';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import PatientDetailsDialog from './patient-details-dialog';
import { notifyAuthExpired, isAuthExpiredDialogVisible, isAuthExpiredError } from '../../../utils/auth-error-utils';

function HandoutEmailButton({
    handoutData,
    currentUser,
    bundledHandoutsView = false,
    accountType,
    onUpgradeRequired,
    toggleStates,
    generateLink,
    shouldShowPatientDialog = () => false,
}) {
    const { userDataStore, authStore } = useContext(StoreContext);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [openErrorDialog, setOpenErrorDialog] = useState(false);
    const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorDialogTitle, setErrorDialogTitle] = useState('Email failed to send');
    const [errorDialogMessage, setErrorDialogMessage] = useState('Please try again');
    const [showReloadAction, setShowReloadAction] = useState(false);

    // Provet integration states
    const [emailsFromProvet, setEmailsFromProvet] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState('');
    const [useManual, setUseManual] = useState(false);
    const [patientsFromProvet, setPatientsFromProvet] = useState([]);
    const [selectedPatientUrl, setSelectedPatientUrl] = useState('');

    // Patient details dialog state
    const [patientDialogOpen, setPatientDialogOpen] = useState(false);
    const [pendingPatientDetails, setPendingPatientDetails] = useState(null);

    const sendEmail = useSendEmail();
    // const theme = useTheme();

    const openAuthAwareErrorDialog = (error) => {
        const isAuthExpired = isAuthExpiredError(error);
        if (isAuthExpired) {
            notifyAuthExpired({ source: 'handout-email' });
            return;
        }
        setErrorDialogTitle('Email failed to send');
        setErrorDialogMessage('Please try again');
        setShowReloadAction(false);
        setOpenErrorDialog(true);
    };

    // Prefill email from ProVet if possible
    const prefillFromProvet = async () => {
        try {
            const client = localStorage.getItem('provet_client');

            if (!client) return;

            const { emails, patients } = await ProvetApi.getClientEmails(authStore.idToken, {
                client,
            });

            setEmailsFromProvet(emails || []);
            setPatientsFromProvet(patients || []);

            if (patients && patients.length === 1) {
                setSelectedPatientUrl(patients[0].url);
            } else {
                setSelectedPatientUrl('');
            }

            if (emails && emails.length === 1) {
                setEmail(emails[0]);
                setSelectedEmail(emails[0]);
                setUseManual(true);
            } else {
                setUseManual(false); // show dropdown when >1
            }
        } catch (e) {
            console.warn('ProVet email prefill failed:', e?.message || e);
        }
    };

    const handleDialogSend = async () => {
        try {
            // Generate the linkId with patient details if available
            const linkId = await generateLink(pendingPatientDetails);
            console.log('>>> Generated linkId:', linkId);
            const clinicId = userDataStore.userId;

            const result = await sendEmail(
                email,
                bundledHandoutsView ? 'your handouts' : handoutData.contentName,
                linkId,
                clinicId,
                toggleStates,
            );

            // Assuming successful response means the email is sent
            if (result) {
                mixpanel.track('HandoutShared', {
                    email: currentUser.attributes.email,
                    shareMethod: 'Send Email',
                    handoutTitle: handoutData.contentName,
                });

                // Only call ProVet API if we have a valid ProVet client context
                const client = localStorage.getItem('provet_client');
                if (client) {
                    try {
                        const userId = localStorage.getItem('provet_user');
                        const timestamp = localStorage.getItem('provet_timestamp');
                        const verification = localStorage.getItem('provet_verify');

                        const patientId = selectedPatientUrl ? selectedPatientUrl.split('/').filter(Boolean).pop() : null;
                        const sentAt = new Date().toLocaleString('en-GB', { timeZone: 'Europe/London', hour12: true });

                        console.log('[ProVet] Adding note - linkId:', linkId, 'clinicId:', clinicId, 'patientId:', patientId);

                        await ProvetApi.addNote(authStore.idToken, {
                            clientId: client,
                            patientId,
                            handoutTitle: bundledHandoutsView ? 'your handouts' : handoutData.contentName,
                            linkId,
                            clinicId,
                            sentAt,
                            userId,
                            timestamp,
                            verification,
                        });
                        console.log('[ProVet] Note added successfully');
                    } catch (e) {
                        console.warn('[ProVet] Failed to add note:', e?.message || e);
                    }
                } else {
                    console.log('[ProVet] Skipping note creation - not in ProVet context');
                }

                setOpenSuccessDialog(true);
                // Clear pending patient details after successful send
                setPendingPatientDetails(null);
            } else {
                openAuthAwareErrorDialog();
            }
        } catch (error) {
            console.error('Error while sending email:', error);
            openAuthAwareErrorDialog(error);
        }
    };

    const handlePatientDetailsSubmit = async (patientDetails) => {
        // Save patient details and proceed to email dialog
        setPendingPatientDetails(patientDetails);
        setPatientDialogOpen(false);

        // Proceed to email dialog with patient details stored
        if (isAuthExpiredDialogVisible()) {
            return;
        }
        setLoading(true);
        try {
            await prefillFromProvet();
            setDialogOpen(true);
        } catch (error) {
            console.error('Error prefilling from ProVet:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClick = async () => {
        if (isAuthExpiredDialogVisible()) {
            return;
        }

        if (accountType === 'Free') {
            onUpgradeRequired();
            return;
        }

        if (accountType === 'Student') {
            onUpgradeRequired();
            return;
        }
        setLoading(true);
        try {
            // Check if we should show patient dialog first (for "My Library" content)
            if (typeof shouldShowPatientDialog === 'function' ? shouldShowPatientDialog() : false) {
                setPatientDialogOpen(true);
            } else {
                // For "My Clinic" content, proceed directly to email dialog
                await prefillFromProvet();
                setDialogOpen(true);
            }
        } catch (error) {
            console.error('Error prefilling from ProVet:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PatientDetailsDialog
                open={patientDialogOpen}
                onClose={() => {
                    setPatientDialogOpen(false);
                    setPendingPatientDetails(null);
                }}
                onSubmit={handlePatientDetailsSubmit}
            />
            <BlockingSpinner open={loading} />
            <CustomToolbarButton
                iconSrc={ASEmailIcon}
                tooltipTitle={bundledHandoutsView ? 'Email handouts' : 'Email handout'}
                onClick={handleClick}
            />

            <ConfirmationDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                onConfirm={handleDialogSend}
                title="Send via email"
                message="Please enter your email address."
                confirmText="Send"
                cancelText="Cancel"
                cancelTextReq={true}
                // show dropdown if we have >1 from ProVet and not in manual mode
                selectOptions={emailsFromProvet}
                selectValue={selectedEmail}
                onSelectChange={(e) => {
                    const v = e.target.value;
                    if (v === '__OTHER__') {
                        setUseManual(true);
                        setSelectedEmail('');
                        setEmail('');
                    } else {
                        setSelectedEmail(v);
                        setEmail(v);
                        setUseManual(false);
                    }
                }}
                selectLabel="Choose an email"
                includeOtherOption={true}
                // shown when manual OR when zero/one email
                showTextField={useManual || emailsFromProvet.length <= 1}
                textFieldLabel="Email address"
                textFieldValue={email}
                onTextFieldChange={(e) => setEmail(e.target.value)}
                validate={ValidationUtils.validateEmail}
                // PATIENT select (second dropdown)
                showSecondarySelect={patientsFromProvet.length > 1}
                secondarySelectOptions={patientsFromProvet.map((p) => `${p.name} (#${p.id})`)}
                secondarySelectValue={
                    selectedPatientUrl
                        ? (patientsFromProvet.find((p) => p.url === selectedPatientUrl)?.name || '') +
                          ` (#${patientsFromProvet.find((p) => p.url === selectedPatientUrl)?.id || ''})`
                        : ''
                }
                onSecondarySelectChange={(e) => {
                    const label = e.target.value;
                    const match = patientsFromProvet.find((p) => label.includes(`#${p.id})`));
                    setSelectedPatientUrl(match ? match.url : '');
                }}
                secondarySelectLabel="Choose a patient"
            />

            <SuccessDialog
                open={openSuccessDialog}
                handleClose={() => {
                    setOpenSuccessDialog(false);
                }}
                title="Email sent"
                message="The email was sent successfully."
            />
            <ErrorDialog
                open={openErrorDialog}
                handleClose={() => {
                    setOpenErrorDialog(false);
                    setShowReloadAction(false);
                }}
                title={errorDialogTitle}
                message={errorDialogMessage}
                primaryActionText={showReloadAction ? 'Reload Page' : null}
                onPrimaryAction={showReloadAction ? () => window.location.reload() : null}
            />
        </>
    );
}

export default HandoutEmailButton;
